import { Calendar } from "@ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover";
import { cn } from "@u/cn";
import { CalendarDays, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface DateFieldProps {
  /** ISO `YYYY-MM-DD` (or empty). */
  value: string;
  onChange: (value: string) => void;
  /** Disable past dates + start the calendar at today. Default true. */
  futureOnly?: boolean;
  /** How many months ahead are navigable. Default 12. */
  monthsAhead?: number;
  /**
   * `inline` renders the calendar directly (booking-style pickers).
   * `popover` renders a compact trigger button that opens the calendar in a
   * popover — for tight layouts (date ranges, grids, inline rows). Default
   * `inline` so existing consumers are unchanged.
   */
  variant?: "inline" | "popover";
  /** popover: text shown on the trigger when no date is selected. */
  placeholder?: string;
  /** popover: id for the trigger (bind a `<FieldLabel htmlFor>` to it). */
  id?: string;
  /** popover: allow clearing back to empty (default true). */
  clearable?: boolean;
  /** popover: disable the trigger. */
  disabled?: boolean;
  className?: string;
}

// Parse/format in LOCAL time so `YYYY-MM-DD` never shifts a day across zones.
function parseISO(v: string): Date | undefined {
  if (!v) return undefined;
  const [y, m, d] = v.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}
function toISO(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Branded date picker. Keeps the same `YYYY-MM-DD` value as a native
 * `<input type="date">` but with consistent, styleable UI. Two variants:
 * `inline` (calendar rendered directly) and `popover` (compact trigger).
 * `futureOnly={false}` allows past dates (backdating, document dates).
 */
export function DateField({
  value,
  onChange,
  futureOnly = true,
  monthsAhead = 12,
  variant = "inline",
  placeholder,
  id,
  clearable = true,
  disabled = false,
  className,
}: DateFieldProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const selected = parseISO(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endMonth = new Date(today.getFullYear(), today.getMonth() + monthsAhead, 1);

  const commit = (d: Date | undefined) => {
    onChange(d ? toISO(d) : "");
    if (variant === "popover") setOpen(false);
  };

  if (variant === "popover") {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            disabled={disabled}
            className={cn(
              "inline-flex h-10 items-center gap-2 rounded-xl border border-input bg-background px-3 text-base md:text-sm transition-colors hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-50",
              !selected && "text-muted-foreground",
              className,
            )}
          >
            <CalendarDays size={14} className="shrink-0 text-muted-foreground" />
            <span className="truncate">
              {selected
                ? new Intl.DateTimeFormat(i18n.language, {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  }).format(selected)
                : (placeholder ?? "—")}
            </span>
            {selected && clearable && (
              <X
                size={13}
                className="ml-auto shrink-0 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  commit(undefined);
                }}
              />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-auto max-w-[calc(100vw-2rem)] overflow-x-auto p-2"
        >
          <Calendar
            mode="single"
            selected={selected}
            onSelect={commit}
            startMonth={futureOnly ? today : undefined}
            endMonth={endMonth}
            disabled={futureOnly ? (d) => d < today : undefined}
            className="rounded-2xl border border-border/40 bg-muted/20 px-4 py-3"
          />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      <Calendar
        mode="single"
        selected={selected}
        onSelect={commit}
        startMonth={futureOnly ? today : undefined}
        endMonth={endMonth}
        disabled={futureOnly ? (d) => d < today : undefined}
        className="relative mx-auto w-fit rounded-2xl border border-border/40 bg-muted/20 px-8 py-4"
        classNames={{
          // Natural-width grid (not w-full) so the month sits centered with
          // symmetric gutters.
          month: "flex flex-col gap-4",
          month_grid: "border-collapse",
          week: "flex mt-2",
          // Deterministic nav: a justify-between bar overlaying the caption, so
          // the arrows flank the label symmetrically on every viewport (the
          // default absolute left-1/right-1 drifts on mobile).
          month_caption: "flex items-center justify-center pt-1 pb-2",
          // Nav anchored to the calendar ROOT, vertically centered, arrows in
          // the side gutters (mid-height of the calendar, not the top).
          nav: "pointer-events-none absolute inset-y-0 inset-x-0 flex items-center justify-between px-1",
          button_previous:
            "pointer-events-auto h-7 w-7 rounded-lg border border-border/30 bg-muted/20 hover:bg-muted/40 flex items-center justify-center transition-colors disabled:opacity-30 disabled:pointer-events-none",
          button_next:
            "pointer-events-auto h-7 w-7 rounded-lg border border-border/30 bg-muted/20 hover:bg-muted/40 flex items-center justify-center transition-colors disabled:opacity-30 disabled:pointer-events-none",
        }}
      />
      {selected && (
        <div className="flex items-center justify-between rounded-lg bg-primary/5 px-3 py-2">
          <span className="text-sm text-foreground">
            {new Intl.DateTimeFormat(i18n.language, { dateStyle: "full" }).format(
              selected,
            )}
          </span>
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label={t("clear")}
            title={t("clear")}
            className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <X size={13} />
          </button>
        </div>
      )}
    </div>
  );
}
